import { useState } from "react";
import { Maximize2, ExternalLink, X } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { BRAND_IMAGES } from "@/data/catalogue";

const FIGURES = [
  {
    key: "sphere",
    src: BRAND_IMAGES.sphere,
    title: "The Bigibila Spiny Sphere™",
    subtitle: "Indigenous Data Governance Architecture",
    body: "A living architecture: Country and Jurisdiction on the outside, protecting concentric rings of Lore, Governance, Community, Permissions, Metadata and Systems around a core of Sacred Knowledge / Cultural Authority. External spines — FPIC, ICIP, IDS, Escalation and Access Control — connect, enforce and uphold the model.",
  },
  {
    key: "architecture",
    src: BRAND_IMAGES.architecture,
    title: "Bigibila Governance Architecture™",
    subtitle: "Business Analysis & Systems Stack for Aboriginal Governance",
    body: "The five-layer Business Analysis stack (Conceptual → Operational → Technical → Executive Reporting → Product Pathway) mapped against the tools & technology stack and the end-to-end architecture flow: Country, Lore, Governance, Community, Permissions, Metadata, Systems, Decision, Audit and Stewardship.",
  },
  {
    key: "catalogue",
    src: BRAND_IMAGES.catalogue,
    title: "Software & Digital Products Catalogue",
    subtitle: "Choosing the right buying pathway",
    body: "The overview map: how the catalogue separates what can be contracted today from controlled licences, bounded pilots and roadmap products — with the Fast Buyer Map showing which product answers which need.",
  },
];

export default function GovernanceModel() {
  const [zoom, setZoom] = useState(null);

  return (
    <div className="mx-auto max-w-6xl px-5 py-14 lg:px-8 lg:py-20">
      <div className="max-w-3xl">
        <div className="text-xs uppercase tracking-widest-xs text-[hsl(var(--brand))]">Governance model</div>
        <h1 className="mt-4 font-display text-4xl font-bold tracking-tight sm:text-5xl">
          Strong foundations. Living governance. Sovereign futures.
        </h1>
        <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
          Every BIGTEK product operationalises the same underlying model: Indigenous governance logic
          rendered as systems architecture, so knowledge stays with its people — for the right reasons,
          at the right time. Explore the full frameworks below.
        </p>
        <p className="mt-4 border-l-2 border-[hsl(var(--brand))] pl-4 text-sm italic leading-relaxed text-muted-foreground">
          “Bigibila does not store knowledge. It honours relationships.” The bigibila (echidna), with its
          protective spines, is the namesake of the Spiny Sphere — a system that shields what matters most.
        </p>
      </div>

      <div className="mt-14 space-y-16">
        {FIGURES.map((f, i) => (
          <figure key={f.key} className="grid gap-8 lg:grid-cols-[1fr_320px] lg:items-start">
            <button
              data-testid={`governance-figure-${f.key}`}
              onClick={() => setZoom(f)}
              className="group relative block overflow-hidden border border-border bg-[hsl(222_47%_7%)] transition-colors duration-200 hover:border-[hsl(var(--brand))]"
            >
              <img
                src={f.src}
                alt={`${f.title} — ${f.subtitle}`}
                className="mx-auto max-h-[70vh] w-full object-contain"
              />
              <span className="absolute right-3 top-3 flex items-center gap-1.5 border border-white/20 bg-black/50 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest-xs text-white/80 backdrop-blur-sm transition-colors group-hover:border-[hsl(var(--brand))] group-hover:text-white">
                <Maximize2 className="h-3 w-3" strokeWidth={2} /> Zoom
              </span>
            </button>

            <figcaption className={`lg:pt-2 ${i % 2 === 1 ? "lg:order-first" : ""}`}>
              <div className="font-mono text-[11px] uppercase tracking-widest-xs text-muted-foreground">
                Figure {i + 1}
              </div>
              <h2 className="mt-3 font-display text-2xl font-medium leading-tight">{f.title}</h2>
              <div className="mt-1 text-sm text-[hsl(var(--brand))]">{f.subtitle}</div>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{f.body}</p>
              <div className="mt-5 flex flex-wrap gap-4">
                <button
                  onClick={() => setZoom(f)}
                  data-testid={`figure-zoom-btn-${f.key}`}
                  className="inline-flex items-center gap-1.5 text-sm text-foreground underline-offset-4 hover:underline"
                >
                  <Maximize2 className="h-4 w-4" strokeWidth={1.75} /> View full size
                </button>
                <a
                  href={f.src}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                >
                  <ExternalLink className="h-4 w-4" strokeWidth={1.75} /> Open original
                </a>
              </div>
            </figcaption>
          </figure>
        ))}
      </div>

      <p className="mt-16 border-t border-border pt-8 text-xs leading-relaxed text-slate-500">
        These frameworks are provided for orientation. They are not cultural certification and do not
        assert authority over any Nation or community. Nation authority and project-specific cultural
        permissions must be independently validated. Trade marks are used as claimed; entity and IP
        status subject to confirmation.
      </p>

      {/* Lightbox */}
      <Dialog open={!!zoom} onOpenChange={(o) => !o && setZoom(null)}>
        <DialogContent
          data-testid="governance-lightbox"
          className="max-w-[96vw] border-border bg-[hsl(222_47%_7%)] p-0 sm:max-w-[92vw]"
        >
          {zoom && (
            <div className="relative">
              <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <div>
                  <div className="font-display text-sm font-medium">{zoom.title}</div>
                  <div className="text-xs text-muted-foreground">{zoom.subtitle}</div>
                </div>
                <button
                  data-testid="lightbox-close"
                  onClick={() => setZoom(null)}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" strokeWidth={1.75} />
                </button>
              </div>
              <div className="max-h-[82vh] overflow-auto p-2 sm:p-4">
                <img src={zoom.src} alt={zoom.title} className="mx-auto h-auto w-full max-w-full" />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
